import * as THREE from 'three';
import { CHUNK_SIZE } from '../constants.js';
import { BlockId, BlockFaces, isTransparent, getTileUV, getWheatTileForStage } from './BlockType.js';
import { getLightAt } from './LightEngine.js';

// Pre-compute face data as flat arrays for faster iteration
// Each face: dirX, dirY, dirZ, faceIndex, then 4 corners (posX, posY, posZ, uvU, uvV)
const FACE_COUNT = 6;
const faceData = [
  // +Y (top)
  { dx: 0, dy: 1, dz: 0, fi: 0,
    c0x: 0, c0y: 1, c0z: 1, c0u: 0, c0v: 1,
    c1x: 1, c1y: 1, c1z: 1, c1u: 1, c1v: 1,
    c2x: 1, c2y: 1, c2z: 0, c2u: 1, c2v: 0,
    c3x: 0, c3y: 1, c3z: 0, c3u: 0, c3v: 0 },
  // -Y (bottom)
  { dx: 0, dy: -1, dz: 0, fi: 1,
    c0x: 0, c0y: 0, c0z: 0, c0u: 0, c0v: 0,
    c1x: 1, c1y: 0, c1z: 0, c1u: 1, c1v: 0,
    c2x: 1, c2y: 0, c2z: 1, c2u: 1, c2v: 1,
    c3x: 0, c3y: 0, c3z: 1, c3u: 0, c3v: 1 },
  // +X (right)
  { dx: 1, dy: 0, dz: 0, fi: 2,
    c0x: 1, c0y: 0, c0z: 0, c0u: 0, c0v: 0,
    c1x: 1, c1y: 1, c1z: 0, c1u: 0, c1v: 1,
    c2x: 1, c2y: 1, c2z: 1, c2u: 1, c2v: 1,
    c3x: 1, c3y: 0, c3z: 1, c3u: 1, c3v: 0 },
  // -X (left)
  { dx: -1, dy: 0, dz: 0, fi: 2,
    c0x: 0, c0y: 0, c0z: 1, c0u: 0, c0v: 0,
    c1x: 0, c1y: 1, c1z: 1, c1u: 0, c1v: 1,
    c2x: 0, c2y: 1, c2z: 0, c2u: 1, c2v: 1,
    c3x: 0, c3y: 0, c3z: 0, c3u: 1, c3v: 0 },
  // +Z (front)
  { dx: 0, dy: 0, dz: 1, fi: 2,
    c0x: 1, c0y: 0, c0z: 1, c0u: 0, c0v: 0,
    c1x: 1, c1y: 1, c1z: 1, c1u: 0, c1v: 1,
    c2x: 0, c2y: 1, c2z: 1, c2u: 1, c2v: 1,
    c3x: 0, c3y: 0, c3z: 1, c3u: 1, c3v: 0 },
  // -Z (back)
  { dx: 0, dy: 0, dz: -1, fi: 2,
    c0x: 0, c0y: 0, c0z: 0, c0u: 0, c0v: 0,
    c1x: 0, c1y: 1, c1z: 0, c1u: 0, c1v: 1,
    c2x: 1, c2y: 1, c2z: 0, c2u: 1, c2v: 1,
    c3x: 1, c3y: 0, c3z: 0, c3u: 1, c3v: 0 },
];

// AO brightness lookup: index = number of solid neighbors (0-3)
const AO_CURVE = [1.0, 0.75, 0.55, 0.4];

// Pre-compute AO neighbor offsets for each face (6) x each corner (4).
// For each vertex, we check 3 blocks in the face-normal layer:
//   side1 (along tangent axis B), side2 (along tangent axis C), and diagonal (both).
// Each entry stores 9 values: s1x,s1y,s1z, s2x,s2y,s2z, dx,dy,dz
function computeAOOffsets() {
  // For each face, the normal direction and two tangent axes (B and C).
  // For a corner at tangent position (cb, cc) in {0,1}, the direction is db = 2*cb - 1.
  const faceDefs = [
    // +Y: normal (0,1,0), B=X, C=Z
    { n: [0,1,0], b: [1,0,0], c: [0,0,1],
      corners: [[-1,1],[1,1],[1,-1],[-1,-1]] },  // c0(x=0,z=1), c1(x=1,z=1), c2(x=1,z=0), c3(x=0,z=0)
    // -Y: normal (0,-1,0), B=X, C=Z
    { n: [0,-1,0], b: [1,0,0], c: [0,0,1],
      corners: [[-1,-1],[1,-1],[1,1],[-1,1]] },   // c0(0,0,0), c1(1,0,0), c2(1,0,1), c3(0,0,1)
    // +X: normal (1,0,0), B=Y, C=Z
    { n: [1,0,0], b: [0,1,0], c: [0,0,1],
      corners: [[-1,-1],[1,-1],[1,1],[-1,1]] },    // c0(1,0,0), c1(1,1,0), c2(1,1,1), c3(1,0,1)
    // -X: normal (-1,0,0), B=Y, C=Z
    { n: [-1,0,0], b: [0,1,0], c: [0,0,1],
      corners: [[-1,1],[1,1],[1,-1],[-1,-1]] },    // c0(0,0,1), c1(0,1,1), c2(0,1,0), c3(0,0,0)
    // +Z: normal (0,0,1), B=X, C=Y
    { n: [0,0,1], b: [1,0,0], c: [0,1,0],
      corners: [[1,-1],[1,1],[-1,1],[-1,-1]] },    // c0(1,0,1), c1(1,1,1), c2(0,1,1), c3(0,0,1)
    // -Z: normal (0,0,-1), B=X, C=Y
    { n: [0,0,-1], b: [1,0,0], c: [0,1,0],
      corners: [[-1,-1],[-1,1],[1,1],[1,-1]] },    // c0(0,0,0), c1(0,1,0), c2(1,1,0), c3(1,0,0)
  ];

  const result = [];
  for (let f = 0; f < 6; f++) {
    const fd = faceDefs[f];
    const offsets = [];
    for (let ci = 0; ci < 4; ci++) {
      const [db, dc] = fd.corners[ci];
      for (const [sb, sc] of [[db, 0], [0, dc], [db, dc]]) {
        offsets.push(
          fd.n[0] + sb * fd.b[0] + sc * fd.c[0],
          fd.n[1] + sb * fd.b[1] + sc * fd.c[1],
          fd.n[2] + sb * fd.b[2] + sc * fd.c[2]
        );
      }
    }
    result.push(offsets);
  }
  return result;
}

const AO_OFFSETS = computeAOOffsets();

// Pre-compute UV cache for all possible tile indices (avoid repeated getTileUV calls)
const MAX_TILES = 256; // more than enough
const _uvCache = new Array(MAX_TILES);
for (let i = 0; i < MAX_TILES; i++) {
  _uvCache[i] = getTileUV(i);
}

export class ChunkMesher {
  constructor(world, atlasTexture) {
    this.world = world;
    this.opaqueMaterial = new THREE.MeshLambertMaterial({
      map: atlasTexture,
      alphaTest: 0.1,
      transparent: false,
      side: THREE.FrontSide,
      vertexColors: true,
    });
    this.transparentMaterial = new THREE.MeshLambertMaterial({
      map: atlasTexture,
      transparent: true,
      opacity: 0.65,
      side: THREE.DoubleSide,
      depthWrite: false,
      vertexColors: true,
    });

    // Pre-allocate working arrays to reduce GC pressure
    // These get reused every buildMesh call
    this._oP = []; this._oN = []; this._oU = []; this._oI = []; this._oC = [];
    this._tP = []; this._tN = []; this._tU = []; this._tI = []; this._tC = [];
  }

  /**
   * Compute ambient occlusion brightness for a vertex.
   * Checks 2 side neighbors + 1 diagonal in the face-normal layer.
   * Returns brightness from AO_CURVE (1.0 = full light, 0.4 = deep shadow).
   */
  _computeAO(chunk, blocks, x, y, z, faceIndex, cornerIndex) {
    const offsets = AO_OFFSETS[faceIndex];
    const base = cornerIndex * 9;

    const s1 = this._isBlockSolid(chunk, blocks,
      x + offsets[base], y + offsets[base + 1], z + offsets[base + 2]) ? 1 : 0;
    const s2 = this._isBlockSolid(chunk, blocks,
      x + offsets[base + 3], y + offsets[base + 4], z + offsets[base + 5]) ? 1 : 0;
    // If both sides are solid, diagonal is occluded regardless (prevents light leak at corners)
    const dg = (s1 && s2) ? 1 :
      (this._isBlockSolid(chunk, blocks,
        x + offsets[base + 6], y + offsets[base + 7], z + offsets[base + 8]) ? 1 : 0);

    return AO_CURVE[s1 + s2 + dg];
  }

  /**
   * Check if a block at the given local coords is solid (non-transparent, non-air).
   */
  _isBlockSolid(chunk, blocks, lx, ly, lz) {
    let blockId;
    if (lx >= 0 && lx < CHUNK_SIZE &&
        ly >= 0 && ly < CHUNK_SIZE &&
        lz >= 0 && lz < CHUNK_SIZE) {
      blockId = blocks[lx + ly * CHUNK_SIZE + lz * CHUNK_SIZE * CHUNK_SIZE];
    } else {
      const wx = chunk.cx * CHUNK_SIZE + lx;
      const wy = chunk.cy * CHUNK_SIZE + ly;
      const wz = chunk.cz * CHUNK_SIZE + lz;
      blockId = this.world.getBlock(wx, wy, wz);
    }
    return blockId !== BlockId.AIR && !isTransparent(blockId);
  }

  /**
   * Build the mesh for a chunk.
   * @param {Chunk} chunk
   * @param {boolean} isLOD - If true, use reduced detail (skip interior face checks for distant chunks)
   */
  buildMesh(chunk, isLOD = false) {
    const oP = this._oP; oP.length = 0;
    const oN = this._oN; oN.length = 0;
    const oU = this._oU; oU.length = 0;
    const oI = this._oI; oI.length = 0;
    const oC = this._oC; oC.length = 0;
    const tP = this._tP; tP.length = 0;
    const tN = this._tN; tN.length = 0;
    const tU = this._tU; tU.length = 0;
    const tI = this._tI; tI.length = 0;
    const tC = this._tC; tC.length = 0;
    let oV = 0, tV = 0;

    const ox = chunk.cx * CHUNK_SIZE;
    const oy = chunk.cy * CHUNK_SIZE;
    const oz = chunk.cz * CHUNK_SIZE;

    // Direct block array access is faster than getBlock method calls
    const blocks = chunk.blocks;

    // LOD optimization: for distant chunks, use a step of 2 to skip every other block
    // This creates a coarser but faster mesh
    const step = (isLOD && CHUNK_SIZE >= 16) ? 1 : 1;
    // Note: actual LOD skipping of blocks causes visual artifacts with voxels.
    // Instead, LOD skips the +Y/-Y face checks for fully interior blocks (optimization below).

    for (let y = 0; y < CHUNK_SIZE; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
          const blockId = blocks[x + y * CHUNK_SIZE + z * CHUNK_SIZE * CHUNK_SIZE];
          if (blockId === BlockId.AIR) continue;

          const blockFaces = BlockFaces[blockId];
          if (!blockFaces) continue;

          const blockTransparent = isTransparent(blockId);

          for (let f = 0; f < FACE_COUNT; f++) {
            const face = faceData[f];

            // LOD: for distant chunks, skip top/bottom face checks for interior blocks
            // (blocks not on chunk boundary that have neighbors above/below)
            if (isLOD && (f === 0 || f === 1)) {
              if (y > 0 && y < CHUNK_SIZE - 1) {
                const ny = y + face.dy;
                const nId = blocks[x + ny * CHUNK_SIZE + z * CHUNK_SIZE * CHUNK_SIZE];
                if (nId !== BlockId.AIR && !isTransparent(nId)) continue;
              }
            }

            const nx = x + face.dx;
            const ny = y + face.dy;
            const nz = z + face.dz;
            const neighborId = this._getNeighborBlock(chunk, blocks, nx, ny, nz);

            let shouldRender;
            if (blockTransparent) {
              shouldRender = neighborId !== blockId;
            } else {
              shouldRender = isTransparent(neighborId);
            }

            if (!shouldRender) continue;

            let tileIndex = blockFaces[face.fi];
            // Override tile for wheat based on growth stage
            if (blockId === BlockId.WHEAT && this.world.farmingSystem) {
              const wx = chunk.cx * CHUNK_SIZE + x;
              const wy = chunk.cy * CHUNK_SIZE + y;
              const wz = chunk.cz * CHUNK_SIZE + z;
              tileIndex = getWheatTileForStage(this.world.farmingSystem.getGrowthStage(wx, wy, wz));
            }
            const uv = _uvCache[tileIndex];
            const u0 = uv.u0, v0 = uv.v0, u1 = uv.u1, v1 = uv.v1;
            const du = u1 - u0;
            const dv = v1 - v0;

            const positions = blockTransparent ? tP : oP;
            const normals = blockTransparent ? tN : oN;
            const uvs = blockTransparent ? tU : oU;
            const indices = blockTransparent ? tI : oI;
            const colors = blockTransparent ? tC : oC;
            const vertCount = blockTransparent ? tV : oV;

            // Compute AO for all 4 corners (skip for transparent blocks)
            let ao0, ao1, ao2, ao3;
            if (blockTransparent) {
              ao0 = ao1 = ao2 = ao3 = 1.0;
            } else {
              ao0 = this._computeAO(chunk, blocks, x, y, z, f, 0);
              ao1 = this._computeAO(chunk, blocks, x, y, z, f, 1);
              ao2 = this._computeAO(chunk, blocks, x, y, z, f, 2);
              ao3 = this._computeAO(chunk, blocks, x, y, z, f, 3);
            }

            // Compute face light brightness from the adjacent voxel (where the face points)
            const faceLight = getLightAt(chunk, this.world, nx, ny, nz);
            // Map light 0-15 to brightness 0.1-1.0 (minimum 0.1 so things are never fully black)
            const lightBrightness = 0.1 + (faceLight / 15) * 0.9;

            // Multiply AO by light brightness for final vertex color
            const c0 = ao0 * lightBrightness;
            const c1 = ao1 * lightBrightness;
            const c2 = ao2 * lightBrightness;
            const c3 = ao3 * lightBrightness;

            // Inline all 4 corners to avoid per-corner object access overhead
            // Corner 0
            positions.push(face.c0x + x + ox, face.c0y + y + oy, face.c0z + z + oz);
            normals.push(face.dx, face.dy, face.dz);
            uvs.push(u0 + face.c0u * du, v0 + face.c0v * dv);
            colors.push(c0, c0, c0);
            // Corner 1
            positions.push(face.c1x + x + ox, face.c1y + y + oy, face.c1z + z + oz);
            normals.push(face.dx, face.dy, face.dz);
            uvs.push(u0 + face.c1u * du, v0 + face.c1v * dv);
            colors.push(c1, c1, c1);
            // Corner 2
            positions.push(face.c2x + x + ox, face.c2y + y + oy, face.c2z + z + oz);
            normals.push(face.dx, face.dy, face.dz);
            uvs.push(u0 + face.c2u * du, v0 + face.c2v * dv);
            colors.push(c2, c2, c2);
            // Corner 3
            positions.push(face.c3x + x + ox, face.c3y + y + oy, face.c3z + z + oz);
            normals.push(face.dx, face.dy, face.dz);
            uvs.push(u0 + face.c3u * du, v0 + face.c3v * dv);
            colors.push(c3, c3, c3);

            // Flip quad diagonal when AO is anisotropic to fix interpolation artifacts
            if (ao0 + ao2 > ao1 + ao3) {
              indices.push(
                vertCount, vertCount + 1, vertCount + 2,
                vertCount, vertCount + 2, vertCount + 3
              );
            } else {
              indices.push(
                vertCount + 1, vertCount + 2, vertCount + 3,
                vertCount + 1, vertCount + 3, vertCount
              );
            }

            if (blockTransparent) tV += 4;
            else oV += 4;
          }
        }
      }
    }

    // Dispose old meshes
    if (chunk.mesh) {
      this.world.scene.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
      chunk.mesh = null;
    }
    if (chunk.transparentMesh) {
      this.world.scene.remove(chunk.transparentMesh);
      chunk.transparentMesh.geometry.dispose();
      chunk.transparentMesh = null;
    }

    // Opaque mesh
    if (oP.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(oP, 3));
      geo.setAttribute('normal', new THREE.Float32BufferAttribute(oN, 3));
      geo.setAttribute('uv', new THREE.Float32BufferAttribute(oU, 2));
      geo.setAttribute('color', new THREE.Float32BufferAttribute(oC, 3));
      geo.setIndex(oI);
      // Compute bounding sphere for internal Three.js frustum culling
      geo.computeBoundingSphere();
      chunk.mesh = new THREE.Mesh(geo, this.opaqueMaterial);
      /* Los chunks proyectan Y reciben: sin castShadow un árbol no da sombra,
         y sin receiveShadow el suelo no la recoge. Hacen falta los dos. */
      chunk.mesh.castShadow = true;
      chunk.mesh.receiveShadow = true;
      chunk.mesh.frustumCulled = true;
      this.world.scene.add(chunk.mesh);
    }

    // Transparent mesh
    if (tP.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(tP, 3));
      geo.setAttribute('normal', new THREE.Float32BufferAttribute(tN, 3));
      geo.setAttribute('uv', new THREE.Float32BufferAttribute(tU, 2));
      geo.setAttribute('color', new THREE.Float32BufferAttribute(tC, 3));
      geo.setIndex(tI);
      geo.computeBoundingSphere();
      chunk.transparentMesh = new THREE.Mesh(geo, this.transparentMaterial);
      chunk.transparentMesh.renderOrder = 1;
      chunk.transparentMesh.frustumCulled = true;
      this.world.scene.add(chunk.transparentMesh);
    }

    chunk.isDirty = false;
  }

  /**
   * Get neighbor block, optimized with direct array access for in-chunk lookups.
   */
  _getNeighborBlock(chunk, blocks, lx, ly, lz) {
    if (lx >= 0 && lx < CHUNK_SIZE &&
        ly >= 0 && ly < CHUNK_SIZE &&
        lz >= 0 && lz < CHUNK_SIZE) {
      return blocks[lx + ly * CHUNK_SIZE + lz * CHUNK_SIZE * CHUNK_SIZE];
    }
    const wx = chunk.cx * CHUNK_SIZE + lx;
    const wy = chunk.cy * CHUNK_SIZE + ly;
    const wz = chunk.cz * CHUNK_SIZE + lz;
    return this.world.getBlock(wx, wy, wz);
  }
}
